import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CsrAPI } from "../../apis/CsrAPI";

// Dedicated slice for SCS (Safety Clinical Summary) data. Kept separate from
// `csrData` because the TFL content served here belongs to the SCS product and
// must never be conflated with the CSR study data. Consumers select from
// `state.scsData` to stay on the SCS branch.
const initialState = {
  summaryId: null,
  // Name shown in the dashboard header on every SCS page. `summaryNameFor` is
  // the summary the name belongs to, so a reset for a *different* summary can
  // drop a stale name while a reset for the same one (the hop from
  // /scs/new/summary to /scs/:summaryId/file-upload) keeps it.
  summaryName: "",
  summaryNameFor: null,
  tflContent: { data: null, loading: false, error: null, fetched: false },
  // Everything answered on the Description of Studies page, kept here so later
  // wizard pages (Data Mapping reads the Q2A grouping names) can work from the
  // user's answers instead of hardcoding them. `summaryIdFor` is the summary the
  // answers belong to — the bucket is summary-scoped in its own right so it
  // survives the reset that follows a route change to the same summary, and is
  // dropped when the reset is for a different one.
  descriptionOfStudies: {
    data: null,
    loading: false,
    error: null,
    fetched: false,
    summaryIdFor: null,
  },
};

// Summary ids arrive as a string from the URL and can arrive as a number from
// the API, so normalise before comparing them.
const toId = value =>
  value === null || value === undefined ? null : String(value);

const isCancellation = err =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

export const fetchScsTflContent = createAsyncThunk(
  "scsData/fetchScsTflContent",
  async (summaryId, { rejectWithValue }) => {
    try {
      return await CsrAPI.getSafetySummaryTflContent({ summaryId });
    } catch (err) {
      if (isCancellation(err)) {
        return rejectWithValue("cancelled");
      }
      return rejectWithValue(err?.message || "Failed to load TFL content");
    }
  },
  {
    condition: (summaryId, { getState }) => {
      if (!summaryId) return false;
      const slice = getState().scsData;
      // Different summary — always allow; the reducers rebind on reset.
      if (slice.summaryId !== summaryId) return true;
      // Already fetched successfully — nothing to do.
      if (slice.tflContent.fetched && !slice.tflContent.error) return false;
      // Already in flight — don't stack a second request.
      if (slice.tflContent.loading) return false;
      return true;
    },
  }
);

// The wizard GET serves the saved answers either bare or wrapped in
// `description_of_studies`; the store always holds the bare section object.
const asDescriptionOfStudies = payload =>
  payload?.description_of_studies || payload || null;

export const fetchScsDescriptionOfStudies = createAsyncThunk(
  "scsData/fetchScsDescriptionOfStudies",
  async (summaryId, { rejectWithValue }) => {
    try {
      return await CsrAPI.getSafetySummaryDescriptionOfStudies({ summaryId });
    } catch (err) {
      if (isCancellation(err)) {
        return rejectWithValue("cancelled");
      }
      // 404 = nothing saved on that page yet, which is a valid answer (no
      // groupings defined) rather than a failure.
      if (err?.response?.status === 404) return null;
      return rejectWithValue(
        err?.message || "Failed to load description of studies"
      );
    }
  },
  {
    condition: (summaryId, { getState }) => {
      if (!summaryId) return false;
      const bucket = getState().scsData.descriptionOfStudies;
      // Different summary — always allow; the reducers rebind on pending.
      if (bucket.summaryIdFor !== toId(summaryId)) return true;
      if (bucket.fetched && !bucket.error) return false;
      if (bucket.loading) return false;
      return true;
    },
  }
);

const scsDataSlice = createSlice({
  name: "scsData",
  initialState,
  reducers: {
    resetScsForSummary: (state, action) => {
      state.summaryId = action.payload;
      if (state.summaryNameFor !== toId(action.payload)) {
        state.summaryName = "";
        state.summaryNameFor = null;
      }
      state.tflContent = {
        data: null,
        loading: false,
        error: null,
        fetched: false,
      };
      // Answers belonging to another summary are stale; a reset for the same
      // summary (the hop from /scs/new/summary onwards) keeps them.
      if (state.descriptionOfStudies.summaryIdFor !== toId(action.payload)) {
        state.descriptionOfStudies = { ...initialState.descriptionOfStudies };
      }
    },
    setScsSummaryName: (state, action) => {
      state.summaryName = action.payload?.summaryName || "";
      state.summaryNameFor = toId(action.payload?.summaryId);
    },
    clearScsSummaryName: state => {
      state.summaryName = "";
      state.summaryNameFor = null;
    },
    // Push the Description of Studies answers into the store from the page
    // itself — on restore and after every successful save — so consumers see
    // what the user just typed without re-fetching.
    setScsDescriptionOfStudies: (state, action) => {
      state.descriptionOfStudies = {
        data: asDescriptionOfStudies(action.payload?.data),
        loading: false,
        error: null,
        fetched: true,
        summaryIdFor: toId(action.payload?.summaryId),
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchScsTflContent.pending, (state, action) => {
        if (state.summaryId !== action.meta.arg) return;
        state.tflContent.loading = true;
        state.tflContent.error = null;
      })
      .addCase(fetchScsTflContent.fulfilled, (state, action) => {
        if (state.summaryId !== action.meta.arg) return;
        state.tflContent.data = action.payload;
        state.tflContent.loading = false;
        state.tflContent.fetched = true;
        state.tflContent.error = null;
      })
      .addCase(fetchScsTflContent.rejected, (state, action) => {
        if (state.summaryId !== action.meta.arg) return;
        // Always clear loading; a cancelled fetch that leaves loading=true
        // would also stop the condition guard from blocking duplicates.
        state.tflContent.loading = false;
        if (action.payload === "cancelled") return;
        state.tflContent.error = action.payload || "Failed to load TFL content";
      })
      .addCase(fetchScsDescriptionOfStudies.pending, (state, action) => {
        const summaryIdFor = toId(action.meta.arg);
        // Fetch for another summary — rebind the bucket to it, dropping answers
        // that belong to the summary we've navigated away from.
        if (state.descriptionOfStudies.summaryIdFor !== summaryIdFor) {
          state.descriptionOfStudies = {
            ...initialState.descriptionOfStudies,
            summaryIdFor,
          };
        }
        state.descriptionOfStudies.loading = true;
        state.descriptionOfStudies.error = null;
      })
      .addCase(fetchScsDescriptionOfStudies.fulfilled, (state, action) => {
        if (state.descriptionOfStudies.summaryIdFor !== toId(action.meta.arg))
          return;
        state.descriptionOfStudies.data = asDescriptionOfStudies(
          action.payload
        );
        state.descriptionOfStudies.loading = false;
        state.descriptionOfStudies.fetched = true;
        state.descriptionOfStudies.error = null;
      })
      .addCase(fetchScsDescriptionOfStudies.rejected, (state, action) => {
        if (state.descriptionOfStudies.summaryIdFor !== toId(action.meta.arg))
          return;
        state.descriptionOfStudies.loading = false;
        if (action.payload === "cancelled") return;
        state.descriptionOfStudies.error =
          action.payload || "Failed to load description of studies";
      });
  },
});

export const {
  resetScsForSummary,
  setScsSummaryName,
  clearScsSummaryName,
  setScsDescriptionOfStudies,
} = scsDataSlice.actions;

export default scsDataSlice.reducer;
